/**
 * Accessible Cart Modal for Shopify (Simplified)
 * ------------------------------------------------
 * Strategy:
 *   1. Intercept Add to Cart CLICK (capture + stopImmediatePropagation)
 *      so the theme's JS never fires
 *   2. CSS class on <body> hides the drawer during our operation
 *   3. Plain /cart/add.js and /cart/change.js calls (no sections param)
 *   4. NO fetch wrapping, NO monkey-patching
 *   5. ONE section refresh when modal closes to sync the drawer
 *
 * Upload to: Assets > accessible-cart-modal.js
 * Include in theme.liquid before </body>:
 *   <script src="{{ 'accessible-cart-modal.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  /* ========================================
     DOM References
     ======================================== */
  var modal = document.getElementById('a11y-cart-modal');
  var backdrop = document.getElementById('a11y-cart-modal-backdrop');
  var closeBtn = document.getElementById('a11y-cart-modal-close');
  var continueBtn = document.getElementById('a11y-cart-modal-continue');
  var itemsContainer = document.getElementById('a11y-cart-modal-items');
  var emptyMessage = document.getElementById('a11y-cart-modal-empty');
  var footerEl = document.getElementById('a11y-cart-modal-footer');
  var totalEl = document.getElementById('a11y-cart-modal-total');
  var countEl = document.getElementById('a11y-cart-modal-count');
  var bannerEl = document.getElementById('a11y-cart-modal-banner');
  var bannerText = document.getElementById('a11y-cart-modal-banner-text');
  var liveRegion = document.getElementById('a11y-cart-modal-live');

  if (!modal) return;

  /* ========================================
     State
     ======================================== */
  var triggerElement = null;
  var justAddedKey = null;
  var updateTimers = {};

  /* ========================================
     Helpers
     ======================================== */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function announce(message) {
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = message; }, 100);
  }

  function escapeHtml(text) {
    var el = document.createElement('span');
    el.textContent = text || '';
    return el.innerHTML;
  }

  /* ========================================
     Loading State
     Shows a spinner on the button + a visible
     loading bar below it + announces to
     screen readers immediately.
     ======================================== */
  var loadingBar = null;
  var loadingButton = null;
  var originalButtonHtml = '';

  function showLoading(button) {
    if (!button) return;
    loadingButton = button;

    // Save original button content and add loading class
    originalButtonHtml = button.innerHTML;
    button.classList.add('a11y-cart-btn-loading');
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-label', 'Adding to cart, please wait');

    // Check if button has light background (needs dark spinner)
    var bgColor = window.getComputedStyle(button).backgroundColor;
    if (bgColor && isLightColor(bgColor)) {
      button.classList.add('a11y-cart-spinner-dark');
    }

    // Wrap existing text in a span for visibility toggle
    var textSpan = document.createElement('span');
    textSpan.className = 'a11y-cart-btn-text';
    textSpan.innerHTML = originalButtonHtml;
    button.innerHTML = '';
    button.appendChild(textSpan);

    // Create and show the loading bar below the button
    var parent = button.closest('.product-form__buttons') ||
                 button.closest('.product-form__submit-wrapper') ||
                 button.closest('.product__submit') ||
                 button.parentElement;

    if (parent && !loadingBar) {
      loadingBar = document.createElement('div');
      loadingBar.className = 'a11y-cart-loading-bar';
      loadingBar.setAttribute('role', 'status');
      loadingBar.setAttribute('aria-live', 'assertive');
      loadingBar.innerHTML = '<span class="a11y-cart-loading-bar__spinner" aria-hidden="true"></span>'
                           + '<span>Adding to cart…</span>';
      parent.parentNode.insertBefore(loadingBar, parent.nextSibling);
    }

    if (loadingBar) {
      loadingBar.classList.add('is-visible');
    }

    // Announce to screen readers immediately
    announce('Adding to cart, please wait.');
  }

  function hideLoading() {
    if (loadingButton) {
      loadingButton.classList.remove('a11y-cart-btn-loading');
      loadingButton.classList.remove('a11y-cart-spinner-dark');
      loadingButton.removeAttribute('aria-busy');
      loadingButton.removeAttribute('aria-label');
      loadingButton.innerHTML = originalButtonHtml;
      loadingButton = null;
      originalButtonHtml = '';
    }

    if (loadingBar) {
      loadingBar.classList.remove('is-visible');
    }
  }

  function isLightColor(rgb) {
    var match = rgb.match(/\d+/g);
    if (!match || match.length < 3) return false;
    var r = parseInt(match[0], 10);
    var g = parseInt(match[1], 10);
    var b = parseInt(match[2], 10);
    // Relative luminance check
    var luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    return luminance > 160;
  }

  /* ========================================
     Plain Cart API (no sections, no tricks)
     ======================================== */
  function addToCartAPI(data) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(data),
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || 'Failed'); });
      return res.json();
    });
  }

  function changeCartItem(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || 'Failed'); });
      return res.json();
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); });
  }

  /* ========================================
     Update header cart count badge
     ======================================== */
  function updateHeaderCount(cart) {
    var n = cart.item_count;
    [
      '.cart-count-bubble span[aria-hidden="true"]',
      '.cart-count-bubble', '.cart-count',
      '#cart-icon-bubble span', '[data-cart-count]',
      '.js-cart-count', '.header__cart-count', '.cart-link__count'
    ].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.hasAttribute('data-cart-count')) el.setAttribute('data-cart-count', n);
        var t = el.querySelector('span') || el;
        if (t.children.length === 0) t.textContent = n;
      });
    });
  }

  /* ========================================
     Render all cart items inside our modal
     ======================================== */
  function renderCart(cart) {
    var items = cart.items || [];

    countEl.textContent = items.length > 0
      ? '(' + cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items') + ')'
      : '';

    if (items.length === 0) {
      itemsContainer.style.display = 'none';
      footerEl.style.display = 'none';
      emptyMessage.style.display = 'block';
      return;
    }

    emptyMessage.style.display = 'none';
    itemsContainer.style.display = 'block';
    footerEl.style.display = 'block';

    var html = '';
    items.forEach(function (item) {
      var hl = item.key === justAddedKey ? ' is-just-added' : '';
      var img = item.image ? item.image.replace(/(\.[^.]+)$/, '_180x180$1') : '';
      var vt = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
      var lp = item.final_line_price || (item.price * item.quantity);

      html += '<div class="a11y-cart-item' + hl + '" role="listitem" data-item-key="' + item.key + '" data-unit-price="' + item.price + '">'
        + '<img class="a11y-cart-item__image" src="' + img + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy"/>'
        + '<div class="a11y-cart-item__content">'
        +   '<div class="a11y-cart-item__top-row">'
        +     '<p class="a11y-cart-item__name">' + escapeHtml(item.product_title) + '</p>'
        +     '<button class="a11y-cart-item__remove" type="button" data-remove-key="' + item.key + '" aria-label="Remove ' + escapeHtml(item.product_title) + ' from cart">'
        +       '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>'
        +     '</button>'
        +   '</div>'
        +   (vt ? '<p class="a11y-cart-item__variant">' + escapeHtml(vt) + '</p>' : '')
        +   '<div class="a11y-cart-item__bottom-row">'
        +     '<div class="a11y-cart-item__qty-controls">'
        +       '<button class="a11y-cart-item__qty-btn a11y-cart-item__qty-btn--decrease" type="button" data-qty-decrease="' + item.key + '" aria-label="Decrease quantity of ' + escapeHtml(item.product_title) + '"' + (item.quantity <= 1 ? ' disabled' : '') + '>'
        +         '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false"><line x1="5" y1="12" x2="19" y2="12"/></svg>'
        +       '</button>'
        +       '<input class="a11y-cart-item__qty-input" type="text" inputmode="numeric" pattern="[0-9]*" value="' + item.quantity + '" data-qty-input="' + item.key + '" aria-label="Quantity of ' + escapeHtml(item.product_title) + ', currently ' + item.quantity + '"/>'
        +       '<button class="a11y-cart-item__qty-btn a11y-cart-item__qty-btn--increase" type="button" data-qty-increase="' + item.key + '" aria-label="Increase quantity of ' + escapeHtml(item.product_title) + '">'
        +         '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'
        +       '</button>'
        +     '</div>'
        +     '<span class="a11y-cart-item__line-price" data-line-price="' + item.key + '">' + formatMoney(lp) + '</span>'
        +   '</div>'
        + '</div></div>';
    });

    itemsContainer.innerHTML = html;
    totalEl.textContent = formatMoney(cart.total_price);

    if (justAddedKey) {
      var el = itemsContainer.querySelector('[data-item-key="' + justAddedKey + '"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    attachItemListeners();
  }

  /* ========================================
     Per-item listeners
     ======================================== */
  function attachItemListeners() {
    itemsContainer.querySelectorAll('[data-remove-key]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        removeItem(this.getAttribute('data-remove-key'));
      });
    });
    itemsContainer.querySelectorAll('[data-qty-decrease]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var k = this.getAttribute('data-qty-decrease');
        var inp = itemsContainer.querySelector('[data-qty-input="' + k + '"]');
        var c = parseInt(inp.value, 10) || 1;
        if (c > 1) updateItemQty(k, c - 1);
      });
    });
    itemsContainer.querySelectorAll('[data-qty-increase]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var k = this.getAttribute('data-qty-increase');
        var inp = itemsContainer.querySelector('[data-qty-input="' + k + '"]');
        var c = parseInt(inp.value, 10) || 1;
        updateItemQty(k, c + 1);
      });
    });
    itemsContainer.querySelectorAll('[data-qty-input]').forEach(function (inp) {
      inp.addEventListener('change', function () {
        var k = this.getAttribute('data-qty-input');
        var v = parseInt(this.value, 10);
        if (isNaN(v) || v < 1) v = 1;
        if (v > 99) v = 99;
        updateItemQty(k, v);
      });
      inp.addEventListener('keydown', function (e) {
        if (['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End'].indexOf(e.key) !== -1) return;
        if (e.key >= '0' && e.key <= '9') return;
        e.preventDefault();
      });
    });
  }

  /* ========================================
     Update item quantity
     ======================================== */
  function updateItemQty(key, newQty) {
    var inp = itemsContainer.querySelector('[data-qty-input="' + key + '"]');
    var dec = itemsContainer.querySelector('[data-qty-decrease="' + key + '"]');
    var lp = itemsContainer.querySelector('[data-line-price="' + key + '"]');
    var row = itemsContainer.querySelector('[data-item-key="' + key + '"]');
    if (!inp) return;

    inp.value = newQty;
    inp.setAttribute('aria-label', inp.getAttribute('aria-label').replace(/currently \d+/, 'currently ' + newQty));
    if (dec) dec.disabled = newQty <= 1;
    if (lp && row) {
      var up = parseInt(row.getAttribute('data-unit-price'), 10) || 0;
      lp.textContent = formatMoney(up * newQty);
    }
    inp.classList.add('is-updating');

    if (updateTimers[key]) clearTimeout(updateTimers[key]);
    updateTimers[key] = setTimeout(function () {
      changeCartItem(key, newQty).then(function (cart) {
        inp.classList.remove('is-updating');
        totalEl.textContent = formatMoney(cart.total_price);
        updateHeaderCount(cart);
        countEl.textContent = '(' + cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items') + ')';

        var si = null;
        (cart.items || []).forEach(function (i) { if (i.key === key) si = i; });
        if (si && lp) lp.textContent = formatMoney(si.final_line_price || si.price * si.quantity);
        announce('Quantity updated to ' + newQty);
      }).catch(function () {
        inp.classList.remove('is-updating');
        announce('Error updating quantity. Please try again.');
      });
    }, 400);
  }

  /* ========================================
     Remove item
     ======================================== */
  function removeItem(key) {
    var row = itemsContainer.querySelector('[data-item-key="' + key + '"]');
    var name = '';
    if (row) {
      var n = row.querySelector('.a11y-cart-item__name');
      if (n) name = n.textContent;
    }
    var allRows = Array.from(itemsContainer.querySelectorAll('.a11y-cart-item'));
    var idx = -1;
    allRows.forEach(function (r, i) { if (r.getAttribute('data-item-key') === key) idx = i; });

    if (row) row.classList.add('is-removing');

    changeCartItem(key, 0).then(function (cart) {
      updateHeaderCount(cart);
      justAddedKey = null;
      renderCart(cart);

      if (cart.items.length === 0) {
        announce('Cart is now empty. ' + name + ' was removed.');
        closeBtn.focus();
        return;
      }
      var rows = itemsContainer.querySelectorAll('.a11y-cart-item');
      var fi = Math.min(idx, rows.length - 1);
      if (fi >= 0 && rows[fi]) {
        var rb = rows[fi].querySelector('.a11y-cart-item__remove');
        if (rb) rb.focus();
      }
      announce(name + ' removed. ' + cart.item_count + ' items remaining.');
    }).catch(function () {
      if (row) row.classList.remove('is-removing');
      announce('Error removing item. Please try again.');
    });
  }

  /* ========================================
     Modal open / close
     ======================================== */
  function openModal() {
    // Switch from "adding" state to "modal open" state
    document.body.classList.remove('a11y-cart-adding');
    document.body.classList.add('a11y-cart-modal-open');

    backdrop.classList.add('is-visible');
    modal.classList.add('is-visible');
    modal.setAttribute('aria-hidden', 'false');
    setTimeout(function () { closeBtn.focus(); }, 50);
    document.addEventListener('keydown', onKeyDown);
    backdrop.addEventListener('click', closeModal);
  }

  function closeModal() {
    backdrop.classList.remove('is-visible');
    modal.classList.remove('is-visible');
    modal.setAttribute('aria-hidden', 'true');
    bannerEl.classList.remove('is-visible');

    document.removeEventListener('keydown', onKeyDown);
    backdrop.removeEventListener('click', closeModal);

    document.body.classList.remove('a11y-cart-modal-open');

    triggerElement = null;
    justAddedKey = null;

    // Reload the page so the cart drawer syncs with the actual cart state.
    // "View Cart" and "Checkout" are <a> links that navigate on their own,
    // so this reload only fires for the X button and Continue Shopping.
    window.location.reload();
  }

  /* ========================================
     Keyboard
     ======================================== */
  function onKeyDown(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeModal(); return; }
    if (e.key === 'Tab') trapFocus(e);
  }

  function trapFocus(e) {
    var sel = 'button:not(:disabled), a[href], input:not(:disabled), [tabindex]:not([tabindex="-1"])';
    var els = modal.querySelectorAll(sel);
    if (!els.length) return;
    var first = els[0], last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  closeBtn.addEventListener('click', closeModal);
  continueBtn.addEventListener('click', closeModal);

  /* ========================================
     ADD TO CART INTERCEPTION

     Single strategy:
     - Capture-phase CLICK on the submit button
     - stopImmediatePropagation kills ALL other handlers
     - Body class added BEFORE any async work
     - CSS hides the drawer instantly
     ======================================== */
  function processAddToCart(form, trigger) {
    triggerElement = trigger;

    // Show loading state IMMEDIATELY (before any async work)
    showLoading(trigger);

    // Block the drawer via CSS immediately
    document.body.classList.add('a11y-cart-adding');

    var formData = new FormData(form);
    var variantId = formData.get('id');
    var qty = parseInt(formData.get('quantity'), 10) || 1;

    if (!variantId) {
      var vi = form.querySelector('select[name="id"], input[name="id"]');
      if (vi) variantId = vi.value;
    }
    if (!variantId) {
      hideLoading();
      document.body.classList.remove('a11y-cart-adding');
      form.submit();
      return;
    }

    var payload = { id: parseInt(variantId, 10), quantity: qty };

    var sp = formData.get('selling_plan');
    if (sp) payload.selling_plan = parseInt(sp, 10);

    var props = {};
    formData.forEach(function (v, k) {
      if (k.indexOf('properties[') === 0) props[k.replace('properties[', '').replace(']', '')] = v;
    });
    if (Object.keys(props).length > 0) payload.properties = props;

    addToCartAPI(payload)
      .then(function (item) {
        justAddedKey = item.key || String(item.variant_id);
        bannerText.textContent = escapeHtml(item.product_title || item.title) + ' added to your cart';
        bannerEl.classList.add('is-visible');
        return getCart();
      })
      .then(function (cart) {
        updateHeaderCount(cart);
        renderCart(cart);
        hideLoading();
        openModal();

        var addedName = '';
        (cart.items || []).forEach(function (i) { if (i.key === justAddedKey) addedName = i.product_title; });
        announce(addedName + ' added to your cart. ' + cart.item_count + ' items total. Press Escape to continue shopping.');
      })
      .catch(function (err) {
        console.error('[A11y Cart] Add to cart error:', err);
        hideLoading();
        document.body.classList.remove('a11y-cart-adding');
        announce('Error adding item. Please try again.');
      });
  }

  function init() {
    // --- Intercept CLICK on Add to Cart buttons (capture phase) ---
    document.addEventListener('click', function (e) {
      // Match submit buttons inside /cart/add forms
      var btn = e.target.closest(
        'form[action*="/cart/add"] button[type="submit"], ' +
        'form[action*="/cart/add"] input[type="submit"], ' +
        'form[action*="/cart/add"] button:not([type]), ' +
        'product-form button[type="submit"], ' +
        'product-form button[name="add"]'
      );

      if (btn) {
        var form = btn.closest('form[action*="/cart/add"]') || btn.closest('product-form form');
        if (form) {
          e.preventDefault();
          e.stopImmediatePropagation();
          processAddToCart(form, btn);
          return;
        }
      }

      // Also catch quick-add buttons with data attributes
      var quickBtn = e.target.closest('[data-product-id][data-variant-id]');
      if (quickBtn) {
        var txt = (quickBtn.textContent || '').trim().toLowerCase();
        if (txt.indexOf('add') !== -1 || txt.indexOf('cart') !== -1) {
          e.preventDefault();
          e.stopImmediatePropagation();

          triggerElement = quickBtn;
          showLoading(quickBtn);
          document.body.classList.add('a11y-cart-adding');

          var vid = parseInt(quickBtn.getAttribute('data-variant-id'), 10);
          if (!vid) { hideLoading(); document.body.classList.remove('a11y-cart-adding'); return; }

          addToCartAPI({ id: vid, quantity: 1 })
            .then(function (item) {
              justAddedKey = item.key || String(item.variant_id);
              bannerText.textContent = escapeHtml(item.product_title || item.title) + ' added to your cart';
              bannerEl.classList.add('is-visible');
              return getCart();
            })
            .then(function (cart) {
              updateHeaderCount(cart);
              renderCart(cart);
              hideLoading();
              openModal();
              announce('Item added. ' + cart.item_count + ' items in cart.');
            })
            .catch(function (err) {
              console.error('[A11y Cart] Quick add error:', err);
              hideLoading();
              document.body.classList.remove('a11y-cart-adding');
            });
        }
      }
    }, true); // CAPTURE PHASE

    // --- Backup: intercept SUBMIT in case theme calls form.requestSubmit() ---
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.action || form.action.indexOf('/cart/add') === -1) return;

      // If we're already handling it (body class is set), just block
      if (document.body.classList.contains('a11y-cart-adding') ||
          document.body.classList.contains('a11y-cart-modal-open')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      var trigger = form.querySelector('[type="submit"], button:not([type="button"])') || document.activeElement;
      processAddToCart(form, trigger);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();